import React, { createContext, useState, useContext, useEffect, ReactNode, useRef } from 'react';
import { RTM } from "@/qrtc_web_rtm/rtm";
import { RTMConfig,RTMMessage } from "@/qrtc_web_rtm/rtmTypes";
import * as consts from "@/config/const";
import { storage } from "@/util";

interface RTMContextProps {
  isRTMConnected: boolean;
  isRTMKicked: boolean;
  connectRTM: (roomId?: string) => void; // roomId 为可选
  disconnectRTM: () => void;
  rtmPublishToPeer: (peerId: string, roomId?: string, message?: string) => void; // roomId 和 message 为可选
  rtmPublish: (roomId?: string, message?: string) => void; // roomId 和 message 为可选
  addEventListener: (type: string, listener: (data: string) => void) => void;
  removeEventListener: (type: string, listener: (data: string) => void) => void;
}


interface RtmConstProp {
    appId?: string;
    userId?: string;
    rtmConfig?: RTMConfig;
  }
  
  // 定义 RTM 配置
  const artmConfig = {
    serverUrl: consts.RTM_SERVERURL, //should add serverUrl in RTMConfig this is differ from agora
    token: consts.RTM_TOKEN,
    encryptionMode: "chacha20",
    cipherKey: "string",
    salt: new Uint8Array(6),
    useStringUserId: true,
    presenceTimeout: 1000,
    logUpload: false,
    logLevel: "verbose",
    cloudProxy: false,
  };

  // 创建上下文
const RTMContext = createContext<RTMContextProps | undefined>(undefined);

interface RTMProviderProps {
  children: ReactNode;
}

// RTMProvider 组件
export const RTMProvider: React.FC<RTMProviderProps> = ({ children }) => {
  const [isRTMConnected, setIsRTMConnected] = useState(false);
  const [isRTMKicked, setIsRTMKicked] = useState(false);
  const [rtmInstance, setRtmInstance] = useState<RTM | null>(null);
  const connectingRef = useRef(false);

    // 连接到 RTM 的方法
  const connectRTM = async () => {
    if (isRTMConnected || rtmInstance || connectingRef.current) return;
    //const websocket = new WebSocket('wss://example.com/socket');
    const logInfo = storage.get(consts.LOGININFOSTORE) ?? {};
    const {
      appId = consts.RTM_APPID,
      userId = logInfo.userId,
      rtmConfig = artmConfig,
    } = {};
    if (!userId) {
      //console.log("not logined ...");
      return;
    }
    connectingRef.current = true;
    try {
      const inst = new RTM(appId, userId, rtmConfig);
      await inst.login();
      // RTM 保留用于会控/通知，不再承载会议聊天消息。
      await inst.subscribe("notification");
      setRtmInstance(inst);
      setIsRTMConnected(true);
      setIsRTMKicked(false);

      // 实时接收字幕
      inst.on('message', (message: string) => {
        // Handle received message here
        handleIncomingMessage(message);
        console.log(message,'有没有收到信息')
      });

      inst?.on('closed', () => {
        console.log('Disconnected from RTM');
        setIsRTMConnected(false);
      });
      inst?.on('closedByServer', () => {
        console.log('Disconnected from RTM');
        setIsRTMKicked(true);
      });
    } finally {
      connectingRef.current = false;
    }
  };

  const disconnectRTM = async () => {
    if (rtmInstance) {
      rtmInstance.close();
      setRtmInstance(null);
      setIsRTMConnected(false);
    }
  }
 
   // 添加事件监听器
  const addEventListener = (type: string, listener: (data: string) => void) => {
    rtmInstance?.addEventListener(type, listener);
  }

  // 移除事件监听器
  const removeEventListener = (type: string, listener: (data: string) => void) => {
    rtmInstance?.removeEventListener(type, listener);
  }

   // 向对等方发送消息
  const rtmPublishToPeer = async (peerId: string, message: string)=>{
    await rtmInstance?.publishToPeer(peerId, message);
  }

    // 向房间发布消息
  const rtmPublish = async (channel: string, message: string)=>{
    await rtmInstance?.publish(channel, message);
  }
  
  // 实时接收字幕
  const handleIncomingMessage = (message: string) => {
    try {
      const parsedMessage = JSON.parse(message);
      if (parsedMessage.action === 'subtitle') {
        const subtitle = parsedMessage.data;
        console.log('Received subtitle:', subtitle);
        // You can now process the subtitle, e.g., update state or UI
      }
    } catch (error) {
      console.error('Error parsing message:', error);
    }
  };

  useEffect(() => {
    return () => {
      if (rtmInstance) {
        rtmInstance.close();
      }
    };
  }, [rtmInstance]);

  useEffect(() => {
    const logInfo = storage.get(consts.LOGININFOSTORE) ?? {};
    const loginToken = window.localStorage.getItem(consts.LOGINTOKEN) || "";
    if (logInfo?.userId && String(loginToken).trim()) {
      void connectRTM();
    }
  }, []);

  return (
    <RTMContext.Provider value={{ 
        isRTMConnected, 
        isRTMKicked, 
        connectRTM, 
        disconnectRTM,
        rtmPublishToPeer,
        rtmPublish,
        addEventListener,
        removeEventListener }}>
      {children}
    </RTMContext.Provider>
  );
};

export const useRTM = (): RTMContextProps => {
  const context = useContext(RTMContext);
  if (!context) {
    throw new Error('useWebSocket must be used within a WebSocketProvider');
  }
  return context;
};
